import { usePatientDetailsLazyQuery } from "$routes/patient/[:patientId]/_lib/__generated__/patient-queries.gql";
import {
  limit,
  offset,
} from "$routes/patient/[:patientId]/_lib/investigations/investigation-activities-provider";
import { inpatientGraphql } from "$routes/patient/[:patientId]/inpatient/_lib/queries";
import type { CreateInvestigationsAncMutation } from "$routes/patient/[:patientId]/inpatient/investigations/_lib/__generated__/use-create-investigation-activities.gql";
import { VITALS_FRAGMENT } from "$routes/patient/[:patientId]/inpatient/vitals/_lib/query";
import { useApolloClient } from "@apollo/client";
import {
  VisitCareEventAction,
  type VisitPatientVital,
  type VisitPatientVitals,
} from "elephant-web/gql";
import { useEffect } from "react";
import {
  InvestigationActivitiesProviderDocument,
  type InvestigationActivitiesProviderQuery,
  type InvestigationActivitiesProviderQueryVariables,
} from "./__generated__/investigation-activities-provider.gql";
import { getOutcomeValuesFromInvestigation } from "./get-investigation-properties";

function buildVisitLineItem(
  activity: CreateInvestigationsAncMutation["createInvestigations"][0],
  loincCode: string,
) {
  // assume only one value per investigation.
  const maybeValue = getOutcomeValuesFromInvestigation(loincCode, activity);

  if (!maybeValue) {
    return undefined;
  }

  return {
    id: `${activity._id}:${loincCode}`,
    meta: { __typename: "Meta", created: activity.outcome?.meta.created },
    value: maybeValue,
    unit: null,
    __typename: "VitalLineItem",
  };
}

/*
  formatCareEventFromInvestigation creates a VisitCareEvent object from an VisitInvestigationActivity.
  The reason we are doing this is because we need to update the visitById query cache with new VisitCareEvent
  objects whilst createInvestigations and completeInvestigations mutations return VisitInvestigationActivity objects
*/
const formatCareEventFromInvestigation = (
  investigationActivity: CreateInvestigationsAncMutation["createInvestigations"][0],
) => ({
  __typename: "VisitCareEvent",
  _id: investigationActivity._id,
  type: investigationActivity.outcome
    ? VisitCareEventAction.InvestigationOutcome
    : VisitCareEventAction.InvestigationRequest,
  created: investigationActivity.meta.created,
  performer: investigationActivity.request.performer,
  investigationActivity,
});

/**
 * @deprecated This function is deprecated in favour of the `InvestigationActivityDataController.writeToCache`.
 * This is because this fn is too 'visit' oriented and was written prior to use adopting federation 2 that allowed
 * for schema merging and therefore we should only be working with a single `InvestigationActivity` type as opposed
 * to both `VisitInvestigationActivity` and `InvestigationActivity` types.
 */
export const useInvestigationsCache = (patientUuid: string) => {
  const apolloClient = useApolloClient();
  const [findPatientQuery, { data: patientDetailsData }] =
    usePatientDetailsLazyQuery({
      variables: { uuid: patientUuid },
    });
  useEffect(() => {
    if (patientUuid) {
      findPatientQuery();
    }
  }, [findPatientQuery, patientUuid]);
  const currentVisit =
    patientDetailsData?.patientByCardId?.patient?.currentVisit;

  /**
   * @deprecated
   * appendInvestigationsToCache consolidates the cache updating logic for
   * appending investigations to a visit's care events and the
   * investigationActivitiesPaginatedOffset query
   */
  const appendInvestigationsToCache = (
    investigationActivities: CreateInvestigationsAncMutation["createInvestigations"],
    careEventOnly = false, // Set to true when completing an investigation request
  ) => {
    if (!investigationActivities?.length) return;

    // If there is a current visit, append the new investigations formatted as care events to the visit cache
    try {
      if (currentVisit) {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({
            __typename: "Visit",
            uuid: currentVisit._id,
          }),
          fields: {
            careEvents(currentValue) {
              const newRefs = investigationActivities.map((investigation) => {
                const newCareEventRef = apolloClient.cache.writeFragment({
                  data: formatCareEventFromInvestigation(investigation),
                  fragment:
                    inpatientGraphql.fragments.investigationCareEventFragment,
                });
                return newCareEventRef;
              });
              return [...currentValue, ...newRefs];
            },
            /*
             * If there are vitals within the cache the below function will be called where
             * we append any investigation results that are considered 'vitals'.
             */
            vitals(existingVitalsRef) {
              const {
                id: _,
                __typename: __,
                ...vitals
              }: VisitPatientVitals = apolloClient.cache.readFragment({
                id: existingVitalsRef.__ref,
                fragment: VITALS_FRAGMENT,
                fragmentName: "VisitPatientVitals",
              });

              Object.values(vitals).forEach((vital) => {
                const vitalKey = apolloClient.cache.identify(vital);
                const existingData: VisitPatientVital =
                  apolloClient.cache.readFragment({
                    id: vitalKey,
                    fragment: VITALS_FRAGMENT,
                    fragmentName: "VisitPatientVitalFragment",
                  });

                const { id: loincCode } = existingData;

                if (!loincCode) {
                  // we've not found anything in the cache
                  // could be due to trying to use `__typename` as the ref, above.
                  return;
                }

                const values = investigationActivities
                  .map((activity) => {
                    if (!activity.outcome) {
                      return false;
                    }

                    return buildVisitLineItem(activity, loincCode);
                  })
                  .filter((el) => el !== undefined);

                if (values.length < 1) {
                  // no values for this vital
                  return;
                }
                // Currently, values are not references so, we can mutate the obj
                // can be checked using: isReference(existingData.values[0]);
                const newData = {
                  ...existingData,
                  values: [...existingData.values, ...values],
                };

                apolloClient.cache.writeFragment({
                  id: vitalKey,
                  data: newData,
                  fragment: VITALS_FRAGMENT,
                  fragmentName: "VisitPatientVitalFragment",
                });
              });

              return existingVitalsRef;
            },
          },
        });
      }
    } catch (_err) {}

    // Append the new investigations to the investigationActivities cache
    if (!careEventOnly) {
      apolloClient.cache.updateQuery<
        InvestigationActivitiesProviderQuery,
        InvestigationActivitiesProviderQueryVariables
      >(
        {
          query: InvestigationActivitiesProviderDocument,
          variables: {
            input: { patientUuid, interOrgFlag: true },
            limit,
            offset,
          },
        },
        (cacheResult) => {
          try {
            // don't try to update the query if its not in the cache
            if (!cacheResult) {
              return;
            }

            const { investigationActivitiesPaginatedOffset } = cacheResult;

            return {
              investigationActivitiesPaginatedOffset: {
                ...investigationActivitiesPaginatedOffset,
                items: investigationActivities,
              },
            };
          } catch (_e) {}
          return cacheResult;
        },
      );
    }
  };

  /**
   * @deprecated
   * removeInvestigationFromCache removes an investigation from a visit's
   * careEvents and the investigationActivitiesPaginatedOffset queries cache
   */
  const removeInvestigationFromCache = (investigationId: string) => {
    try {
      apolloClient.cache.evict({
        id: apolloClient.cache.identify({
          __typename: "VisitCareEvent",
          _id: investigationId,
          type: "INVESTIGATION_REQUEST",
        }),
      });
    } catch (_err) {}

    try {
      apolloClient.cache.evict({
        id: apolloClient.cache.identify({
          __typename: "InvestigationActivity",
          _id: investigationId,
        }),
      });
    } catch (_err) {}

    apolloClient.cache.gc();
  };

  return { appendInvestigationsToCache, removeInvestigationFromCache };
};
